import React, { useCallback, useEffect, useRef, useState } from "react";
import BackButton from "../../component/backButton/BackButton";
import ChatPage from "pages/ChatPage/ChatPage";
import RecentSearches from "component/recentSearches/RecentSearches";
import Prompts from "component/prompts/Prompts";
import AutoCompleteInput from "component/autoCompleteInput/AutoCompleteInput";
import ChatContainer from "component/chatContainer/ChatContainer";
import useIsAbove1200px from "hooks/useIsAbove1200px";
import MicrophoneButton from "../../component/microphoneButton/MicrophoneButton";
import { AiOutlineClockCircle } from "react-icons/ai";
import { HiOutlineLightBulb } from "react-icons/hi";
import { Globalstate } from "context/context";
import "../../component/inputField/inputField.css";
import { nanoid } from "nanoid";
import { condition } from "context/condition";
import { createBotMessage, createUserMessage } from "utils/utils";
import { useWebSocket } from "context/WebSocketProvider";
import useQna from "hooks/useQna";
import LoadingScreen from "component/loadingScreen/LoadingScreen";
import "./homePage.css";
import { APP_NUDGE_URL } from "config";
import starIcon from "assets/photos/staricon.png"
const HomePage = () => {
  const urlSource = new URLSearchParams(window.location.search);
  const [isFocused, setIsFocused] = useState(false);
  const [activeTab, setActiveTab] = useState(1);
  const [loading, setLoading] = useState(true);
  const [nudgeId, setNudgeId] = useState(
    sessionStorage.getItem("nudgeId") || null
  );
  const [isChatLoading, setIsChatLoading] = useState(false);
  const urlParams = urlSource.get("source");

  const abortControllerRef = useRef(null);
  const isAbove1200px = useIsAbove1200px();
  const { state, dispatch } = Globalstate();
  const { messages } = state;
  const hasRunFetchNudge = useRef(false);

  const tab = {
    0: <RecentSearches handleSubmit={handleSubmit} />,
    1: <Prompts handleSubmit={handleSubmit} />
  };

  const handleRecentButtonClick = () => {
    setActiveTab(0);
  };
  const handlePromptsButtonClick = () => {
    setActiveTab(1);
  };

  const inputRef = useRef(null);
  const [value, setValue] = useState("");
  const [showCrossIcon] = useState(false);
  const { isMessageSubmitted, mode } = state;
  const { sendMessage } = useWebSocket();
  const { mutateAsync: mutateAsyncQna } = useQna();

  async function handleSubmit(value, lang_code) {
    if (value === "") return;
    if (isMessageSubmitted) return;
    const userKey = nanoid();
    const botKey = nanoid();
    inputRef.current.blur();
    setIsChatLoading(true);
    setValue("");
    dispatch({ type: condition.ISMESSAGESUBMITTED, payload: true });
    const userMessage = createUserMessage(value, "", userKey);
    const botMessage = createBotMessage("", botKey, "", [], [], "");
    dispatch({ type: condition.SETMESSGES, payload: userMessage });
    dispatch({ type: condition.SETMESSGES, payload: botMessage });
    abortControllerRef.current = new AbortController();
    if (mode === "qna" || state?.nudgeFlow) {
      await mutateAsyncQna({
        searchTerm: value,
        botKey,
        source: state.source,
        lang_code: lang_code ? lang_code : "en-IN",
        nudgeFlow: state?.nudgeFlow,
        signal: abortControllerRef.current.signal,
      });
      dispatch({ type: condition.ISMESSAGESUBMITTED, payload: false });
    } else if (mode === "stream") {
      sendMessage({
        query: value,
        botKey: botKey,
        source: state.source,
        lang_code: lang_code ? lang_code : "en-IN",
        nudgeFlow: state?.nudgeFlow
      });
      dispatch({ type: condition.ISMESSAGESUBMITTED, payload: false });
    }
    dispatch({ type: condition.SETTRANSCRIPTION, payload: "" });
  }
  const handlePause = () => {

    if (abortControllerRef.current) {
      abortControllerRef.current.abort(); // abort the request
      abortControllerRef.current = null; // clear the ref
      setIsChatLoading(false);
      const botKey = nanoid();
      const botMessage = createBotMessage("", botKey, "Oops! This request was stopped.", [], [], Date.now());
      dispatch({ type: condition.SETMESSGES, payload: botMessage });
      dispatch({ type: condition.ISMESSAGESUBMITTED, payload: false });
    }
  };

  const handleTranscriptionComplete = (result) => {
    console.log("Transcribed Text:", result.text);
    console.log("Detected Language:", result.language);
  };

  useEffect(() => {
    setTimeout(() => {
      setLoading(false);
    }, 500);
  });

  /*
   * Function to generate markdown response from a given Nudge API response.
   * @param {Object} response - Nudge API response object.
   * @returns {String} Markdown formatted response.
   * Note: This is a placeholder function and should be replaced with actual logic to generate markdown response based on the Nudge API response.
   * Example:
   * const markdown = generateMarkdownResponse({
   *   Nudge: "Nudge Message",
   *   Message: {
   *     "Product Push": "Product Push Link",
   *     "Tip": "Product Title"
   *     // Add more fields as needed
   *   }
   * });
   * console.log(markdown); // Output: > Nudge Message\n\n**Product Title**: Product Title\n**Product Description**: Product Description\n[Product Push Link](https://dummy-link.com)
   */
  const generateMarkdownResponse = (response) => {
    // Input validation

    if (!response || typeof response !== "object") {
      console.error("Invalid response object provided");
      return "";
    }

    try {
      // Parse nudge with error handling
      let nudgeData = {};
      if (response.nudge) {
        try {
          nudgeData = JSON.parse(response.nudge);
        } catch (error) {
          console.warn("Failed to parse nudge JSON:", error);
          nudgeData = { Nudge: response.nudge };
        }
      }

      // Extract core data
      const nudge = nudgeData?.Nudge || "";
      const message = nudgeData?.Message || {};
      // const cta_results = Array.isArray(response.cta_results) && response.link;

      // Build markdown sections
      const sections = [];

      // Add nudge if present
      if (nudge) {
        sections.push(`> ${nudge.trim()}`);
      }

      // Add message content excluding Product Push
      const messageEntries = Object.entries(message)
        .filter(([key]) => key !== "Product Push")
        .map(([key, value]) => `**${key}**: ${value}`);

      if (messageEntries.length) {
        sections.push(messageEntries.join("\n"));
      }

      // Add Product Push with link if either exists
      if (message["Product Push"]) {
        const productPush = message["Product Push"] || "";
        sections.push("\n" + productPush);
      }

      // Join sections with double newlines
      return sections.join("\n\n");
    } catch (error) {
      console.error("Error generating markdown:", error);
      return "";
    }
  };

  const fetchChatHistory = useCallback(async () => {
    try {
      const authToken = localStorage.getItem("authToken");
      const myHeaders = new Headers();
      myHeaders.append("Authorization", `Bearer ${authToken}`);
      myHeaders.append("Content-Type", "application/json");

      // Determine which flow and payload to use
      const isNudgeFlow = state.nudgeFlow || urlParams === "nudge";
      const isDigiGoldFlow = state.digiGoldFlow;

      const isNudgeSource = isNudgeFlow || isDigiGoldFlow;

      const source =
        urlParams === "service"
          ? "service"
          : isNudgeSource
            ? "nudge"
            : "simplifi";
      const raw = JSON.stringify({
        nudge_id: isNudgeSource ? nudgeId : "",
        source,
        previous_conversation_count: 20,
      });

      const requestOptions = {
        method: "POST",
        headers: myHeaders,
        body: raw,
        redirect: "follow",
      };

      const response = await fetch(
        `${APP_NUDGE_URL}/get_conversation_history`,
        requestOptions
      );

      if (!response.ok) {
        throw new Error("Failed to fetch chat history");
      }

      const data = await response.json();

      if (data?.history && Array.isArray(data.history)) {
        data.history.forEach((msg) => {
          const userKey = nanoid();
          const botKey = nanoid();
          const userMessage = createUserMessage(msg.user, "", userKey);
          const botMessage = createBotMessage("", botKey, msg.bot, [], [], msg.id);
          dispatch({ type: condition.SETMESSGES, payload: userMessage });
          dispatch({ type: condition.SETMESSGES, payload: botMessage });
        });
      }
    } catch (err) {
      console.error("Error fetching chat history:", err);
    }
  }, [nudgeId, dispatch, state.nudgeFlow, state.digiGoldFlow]);

  // ---------- Call Nudge API ----------
  // const callNudgeApiHere = useCallback(async () => {
  //   const authToken = localStorage.getItem("authToken");
  //   const nudgeApiCall = async () => {
  //     const myHeaders = new Headers();
  //     myHeaders.append("Authorization", `Bearer ${authToken} `);

  //     const requestOptions = {
  //       method: "GET",
  //       headers: myHeaders,
  //       redirect: "follow"
  //     };

  //     try {
  //       const response = await fetch(
  //         `${APP_NUDGE_URL}/fetch_nudge`,
  //         requestOptions
  //       );
  //       const data = await response.json();
  //       if (data.length > 0) {
  //         try {
  //           const value = data[0];
  //           return value;
  //         } catch (e) {
  //           console.error("Failed to parse nudge data", e);
  //           return null;
  //         }
  //       } else {
  //         return null;
  //       }
  //     } catch {
  //       console.error("Failed to fetch nudge data");
  //       return null;
  //     }
  //   };

  //   const response = await nudgeApiCall();
  //   if (state.nudgeFlow && response) {
  //     if (response?.id) {
  //       sessionStorage.setItem("nudgeId", response.id);
  //       setNudgeId(response.id);
  //     }

  //     const botKey = nanoid();
  //     const botMessage = createBotMessage("", botKey, "", [], [], "");
  //     dispatch({ type: condition.SETMESSGES, payload: botMessage });
  //     dispatch({ type: condition.ISMESSAGESUBMITTED, payload: false });

  //     const nudgeresponse = {
  //       answer: generateMarkdownResponse(response),
  //       cta_results: response?.cta_results,
  //       follow_up_flag: false,
  //       id: response?.id,
  //       recommended_questions: [],
  //       unavailability_flag: false,
  //       flag: "true"
  //     };
  //     dispatch({
  //       type: condition.UPDATELASTMESSAGE,
  //       payload: {
  //         ...{ ...nudgeresponse, isBingResponse: false },
  //         key: botKey
  //       }
  //     });
  //   }
  // }, [state.nudgeFlow]);

  const callNudgeApiHere = useCallback(async () => {
    const authToken = localStorage.getItem("authToken");

    // Shared headers
    const myHeaders = new Headers();
    myHeaders.append("Authorization", `Bearer ${authToken}`);
    myHeaders.append("Content-Type", "application/json");

    let responseData = null;

    try {
      if (state.nudgeFlow) {
        // ======= FETCH NUDGE FLOW =======
        const res = await fetch(`${APP_NUDGE_URL}/fetch_nudge`, {
          method: "GET",
          headers: myHeaders,
          redirect: "follow",
        });

        const data = await res.json();

        // your API returns an array → pick first item safely
        if (Array.isArray(data) && data.length > 0) {
          // responseData = data[0];
          responseData = {
            ...data[0],
            sources: "fetch_nudge"
          };

          // set nudgeId safely
          if (responseData?.id) {
            sessionStorage.setItem("nudgeId", responseData.id);
            setNudgeId(responseData.id);
            console.log("✅ Nudge ID set:", responseData.id);
          } else {
            console.warn("⚠️ fetch_nudge response has no ID:", responseData);
          }
        } else {
          console.warn("⚠️ fetch_nudge returned empty array");
        }
      }

      if (state.digiGoldFlow) {
        // ======= DIGI GOLD FLOW =======
        const body = JSON.stringify({
          page: "homescreen",
          application: "gold", // or "silver"
        });

        const res = await fetch(`${APP_NUDGE_URL}/get_DigiGoldSilver_nudge`, {
          method: "POST",
          headers: myHeaders,
          body,
          redirect: "follow",
        });

        const data = await res.json();
        responseData = data;

        // set ID if exists
        if (responseData?.id) {
          sessionStorage.setItem("nudgeId", responseData.id);
          setNudgeId(responseData.id);
          console.log("✅ DigiGold Nudge ID set:", responseData.id);
        }
      }

      // ======= COMMON HANDLING =======
      if (responseData) {
        const botKey = nanoid();
        const botMessage = createBotMessage("", botKey, "", [], [], "");
        dispatch({ type: condition.SETMESSGES, payload: botMessage });
        dispatch({ type: condition.ISMESSAGESUBMITTED, payload: false });

        const nudgeresponse = {
          answer: generateMarkdownResponse(responseData),
          cta_results: responseData?.cta_results,
          follow_up_flag: false,
          id: responseData?.id,
          recommended_questions: [],
          unavailability_flag: false,
          flag: "true",
          sources: "fetch_nudge"
        };

        dispatch({
          type: condition.UPDATELASTMESSAGE,
          payload: {
            ...{ ...nudgeresponse, isBingResponse: false },
            key: botKey,
          },
        });
      } else {
        console.log("No responseData available after API calls");
      }
    } catch (error) {
      console.error(" Error in callNudgeApiHere:", error);
    }
  }, [state.nudgeFlow, state.digiGoldFlow, dispatch, setNudgeId]);


  // useEffect(() => {
  //   const init = async () => {
  //     if (!loading) {
  //       if (state?.nudgeFlow) {
  //         if (!hasRunFetchNudge.current) {
  //           hasRunFetchNudge.current = true;
  //           await callNudgeApiHere();
  //         }
  //       }
  //       await fetchChatHistory();
  //     }
  //   };

  //   init();
  // }, [loading, callNudgeApiHere, fetchChatHistory, state?.nudgeFlow]);
  const getPageFromToken = () => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) return null;

      const payload = JSON.parse(atob(token.split(".")[1]));
      return payload?.page || null;
    } catch {
      return null;
    }
  };

  useEffect(() => {
    const init = async () => {
      if (loading) return;

      const page = getPageFromToken();
      const shouldSkipNudge = page === "Gold" || page === "Silver";

      if (
        ((state?.nudgeFlow || state?.digiGoldFlow) || urlParams === "nudge") &&
        !hasRunFetchNudge.current
        // !shouldSkipNudge
      ) {
        hasRunFetchNudge.current = true;
        await callNudgeApiHere();
      }

      await fetchChatHistory();
    };

    init();
  }, [loading, callNudgeApiHere, fetchChatHistory, state?.nudgeFlow, state?.digiGoldFlow,]);

  return loading ? (
    <LoadingScreen />
  ) : (
    <>
      {isAbove1200px !== null && isAbove1200px ? (
        <div className="homepage-container dt-homepage-container">
          {!state?.nudgeFlow && (
            <BackButton
              style={{
                minHeight: "44px",
                minWidth: "44px",
                padding: "6px"
              }}
              parentStyle={{
                position: "absolute",
                top: "0.5rem",
                left: "2.5rem",
                zIndex: "10"
              }}
              showSimplifiText={false}
            />
          )}
          <div
            className="homepage-left-container"
            style={{
              padding: messages?.length !== 0 ? "4rem 0rem 3rem 3rem" : "0"
            }}
          >
            {messages.length !== 0 ? (
              <>
                <div className="tab-container">
                  <button
                    className={`tab-button ${activeTab === 1 ? "active-button" : "secondary-button"
                      }`}
                    onClick={handlePromptsButtonClick}
                  >
                    <HiOutlineLightBulb />
                    <p>Prompts</p>
                  </button>
                  <button
                    className={`tab-button ${activeTab === 0 ? "active-button" : "secondary-button"
                      }`}
                    onClick={handleRecentButtonClick}
                  >
                    <AiOutlineClockCircle />
                    <p>Recent Searches</p>
                  </button>
                </div>
                {tab[activeTab]}
              </>
            ) : (
              <div className="dtChatPageBg">
                <p>
                  Your AI friend welcomes <br /> you...
                </p>
                <p>Ready to explore more?</p>
              </div>
            )}
          </div>
          <div className="homepage-right-container">
            <div className="homepage-right-container-inner">
              {messages.length === 0 ? (
                <>
                  <div className="right-container-image">
                    <Prompts handleSubmit={handleSubmit} />
                  </div>
                  {
                    state?.nudgeFlow ?
                      <div
                        className="ai-generated-section"
                      >
                        <img
                          src={starIcon}
                          alt="star"
                        />
                        AI-generated insights, not financial advice
                      </div> : ''
                  }
                  <div className="input-field">
                    <AutoCompleteInput
                      setIsFocused={setIsFocused}
                      handleSubmit={handleSubmit}
                      inputRef={inputRef}
                      value={value}
                      setValue={setValue}
                      showCrossIcon={showCrossIcon}
                      handlePause={handlePause}
                      isChatLoading={isChatLoading}
                    />
                    <MicrophoneButton
                      handleSubmit={handleSubmit}
                      handleTranscriptionComplete={handleTranscriptionComplete}
                    />
                  </div>
                </>
              ) : (
                <ChatPage
                  handleSubmit={handleSubmit}
                  setIsFocused={setIsFocused}
                  inputRef={inputRef}
                  value={value}
                  setValue={setValue}
                  showCrossIcon={showCrossIcon}
                  handlePause={handlePause}
                />
              )}
            </div>
          </div>
        </div>
      ) : (
        <>
          {messages.length > 0 ? (
            <ChatContainer
              handleSubmit={handleSubmit}
              setIsFocused={setIsFocused}
              inputRef={inputRef}
              value={value}
              setValue={setValue}
              showCrossIcon={showCrossIcon}
              handlePause={handlePause}
            />
          ) : (
            <div className="homepage-container">
              {!state?.nudgeFlow && (
                <div className="homepage-header">
                  <BackButton />
                  <div className="homepage-header-bottom">
                    <p className="sub-text">Your AI friend</p>
                    <p className="sub-text">welcomes you...</p>
                    <p className="sub-text">Ready to explore more?</p>
                  </div>
                </div>
              )}
              <div className="homepage-body">
                {!state?.nudgeFlow && (
                  <div>
                    <Prompts
                      handleSubmit={handleSubmit}
                      lastFiveSearches={state.recentSearches}
                      isFocused={isFocused}
                    />
                  </div>
                )}
                {
                  state?.nudgeFlow ?
                    <div
                      className="ai-generated-section"
                    >
                      <img
                        src={starIcon}
                        alt="star"
                      />
                      AI-generated insights, not financial advice
                    </div> : ''
                }
                <div className="input-field">
                  <AutoCompleteInput
                    setIsFocused={setIsFocused}
                    handleSubmit={handleSubmit}
                    inputRef={inputRef}
                    value={value}
                    setValue={setValue}
                    showCrossIcon={showCrossIcon}
                    handlePause={handlePause}
                    isChatLoading={isChatLoading}
                  />
                  <MicrophoneButton
                    handleSubmit={handleSubmit}
                    handleTranscriptionComplete={handleTranscriptionComplete}
                  />
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default HomePage;
